import { Component, DestroyRef, inject, ViewChild, ViewEncapsulation } from "@angular/core";
import { DemoAssetService } from "../services/demo-asset.service";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, finalize, map, of, switchMap } from "rxjs";
import { DemoTemplateViewModel, VmImage } from "../core/model/ContentManagement";
import { DemoTemplateService } from "../services/demo-template.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ContentReadyEvent } from "devextreme/ui/data_grid";
import { DemosGridComponent } from "../shared/demos-grid/demos-grid.component";
import { brdCatchError } from "../shared/message-utils";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "brd-demo-template-editor",
  templateUrl: "./demo-template-editor.component.html",
  styleUrl: "./demo-template-editor.component.less",
  encapsulation: ViewEncapsulation.None
})
export class DemoTemplateEditorComponent {
  @ViewChild(DemosGridComponent) demoAssetGrid;
  destroyRef = inject(DestroyRef);

  loading = false;
  submitting = false;

  title = "";
  templateId = 0;
  formGroup?: FormGroup;
  selectedDemos: number[];
  selectedTemplate: DemoTemplateViewModel;

  vmImages: VmImage[] = [];
  assets$ = this.demoAssetService.getAssets();

  constructor(
    private demoAssetService: DemoAssetService,
    private activatedRoute: ActivatedRoute,
    private demoTemplateService: DemoTemplateService,
    private router: Router
  ) {
    this.loading = true;

    this.activatedRoute.params
      .pipe(
        takeUntilDestroyed(),
        map(res => {
          return res["id"] ? parseInt(res["id"]) : 0;
        }),
        switchMap(id => {
          this.templateId = id;

          let obs = of({} as DemoTemplateViewModel);
          if (id) {
            obs = this.demoTemplateService.getDemoTemplate(id);
          }

          return combineLatest([obs, this.demoTemplateService.getTemplateForm()]);
        }),
        map(res => {
          const [template, form] = res;
          this.title = template?.name ? `Edit ${template.name}` : "New template";
          this.selectedTemplate = template;

          const latestImage = form?.vmImages?.find(x => x.latest)?.id ?? null;

          this.formGroup = new FormGroup({
            id: new FormControl(template?.id ?? 0),
            name: new FormControl(template?.name ?? "", Validators.required),
            description: new FormControl(template?.description ?? ""),
            vmImageId: new FormControl(template?.vmImageId ?? latestImage, Validators.required)
          });

          this.selectedDemos = template?.demoAssets ?? [];

          this.vmImages = form?.vmImages ?? [];
        })
      )
      .subscribe(() => {
        this.loading = false;
      });
  }

  cancel(): void {
    this.router.navigate(["/admin-portal", { selectedTab: 1 }]);
  }

  saveAsset(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.value;
    const selectedDemos = this.demoAssetGrid.grid.instance.getSelectedRowKeys();
    this.submitting = true;

    this.demoTemplateService
      .addOrUpdateTemplate({
        id: this.templateId ?? 0,
        name: formValue.name,
        description: formValue.description,
        version: "",
        estimatedTime: 0,
        isEnabled: true,
        vmImageId: formValue.vmImageId,
        demoAssets: selectedDemos,
        creationDate: this.selectedTemplate?.creationDate,
        owner: this.selectedTemplate?.owner,
        group: this.selectedTemplate?.group,
        updateBy: this.selectedTemplate?.updateBy,
        updateDate: this.selectedTemplate?.updateDate
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        brdCatchError(),
        finalize(() => {
          this.submitting = false;
        })
      )
      .subscribe(() => {
        this.router.navigate(["/admin-portal", { selectedTab: 1 }]);
      });
  }

  onGridContentReady(e: ContentReadyEvent): void {
    e.component.selectRows(this.selectedDemos, false);
  }
}
